/**
 * A player's face (§ F120).
 *
 * Three shapes, in order of preference: a preset glyph, a drawing they made, or
 * the generic person icon that means "no avatar yet". All three are drawn on
 * the player's own session colour, so the tile still identifies them at a
 * glance even before anybody picks a face.
 *
 * The drawn case reuses the party surface's stroke format (`Drawing`, from
 * `party/drawing.ts`) because that is the pad the family already draws with —
 * one format, one canvas, two homes.
 */

import { UserCircle2 } from "lucide-react";
import { avatarPreset } from "../lib/avatars";
import type { AvatarValue } from "../lib/roster";
import { sessionColorFor } from "../party/palette";

type Stroke = { c?: string; w?: number; pts?: number[] };
type Drawing = { w?: number; h?: number; strokes?: Stroke[] };

function DrawnAvatar({ drawing, px }: { drawing: Drawing; px: number }) {
  const width = drawing.w && drawing.w > 0 ? drawing.w : 1000;
  const height = drawing.h && drawing.h > 0 ? drawing.h : 1000;
  const strokes = Array.isArray(drawing.strokes) ? drawing.strokes : [];
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={px}
      height={px}
      aria-hidden="true"
      className="block"
    >
      {strokes.map((stroke, i) => {
        const pts = Array.isArray(stroke.pts) ? stroke.pts : [];
        if (pts.length < 4) return null;
        let d = `M ${pts[0]} ${pts[1]}`;
        for (let p = 2; p + 1 < pts.length; p += 2) d += ` L ${pts[p]} ${pts[p + 1]}`;
        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={stroke.c ?? "#000000"}
            strokeWidth={stroke.w ?? 8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
    </svg>
  );
}

export function PlayerAvatar({
  playerId,
  avatar,
  color,
  ink,
  size = 40,
  className = "",
}: {
  playerId: string;
  avatar: AvatarValue | null | undefined;
  /**
   * § F122 — the player's CHOSEN colours. Jake: *"Player avatars are all blue,
   * allow users to pick the avatar, primary color (currently blue) and
   * secondary color (currently black)."* They were all one colour because the
   * colour was derived from the id; the derived tone is the DEFAULT now, and a
   * choice overrides it.
   */
  color?: string | null;
  ink?: string | null;
  /** Box size in px. The glyph is drawn at ~55% of it. */
  size?: number;
  className?: string;
}) {
  const derived = sessionColorFor(playerId);
  const tone = { hex: color ?? derived.hex, ink: ink ?? derived.ink };
  const preset = avatar?.kind === "preset" ? avatarPreset(avatar.preset) : null;
  const glyphPx = Math.round(size * 0.55);

  return (
    <span
      data-testid="player-avatar"
      data-avatar={preset ? preset.id : avatar?.kind === "drawn" ? "drawn" : "none"}
      className={`shrink-0 rounded-block ink-border flex items-center justify-center overflow-hidden ${className}`}
      style={{ width: size, height: size, background: tone.hex, color: tone.ink }}
    >
      {preset ? (
        <preset.glyph className="" strokeWidth={2.5} style={{ width: glyphPx, height: glyphPx }} />
      ) : avatar?.kind === "drawn" ? (
        <DrawnAvatar drawing={(avatar.drawing ?? {}) as Drawing} px={size - 4} />
      ) : (
        <UserCircle2 strokeWidth={2.5} style={{ width: glyphPx, height: glyphPx }} />
      )}
    </span>
  );
}

export default PlayerAvatar;
